import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import i18n from '../i18n';

import { API } from '../config/api';

// Configure axios defaults
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const applyLanguage = (userData) => {
    if (userData && userData.language) {
      i18n.changeLanguage(userData.language);
    }
  };

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      applyLanguage(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    applyLanguage(data);
    return data;
  };

  const register = async (email, password, name, companyName, language, plan) => {
    const { data } = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      company_name: companyName,
      language: language || 'it',
      plan: plan || 'preventivi_ai'
    });
    setUser(data);
    applyLanguage(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(false);
    i18n.changeLanguage('it'); // reset to default
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function formatApiError(error) {
  const detail = error?.response?.data?.detail;
  if (detail == null) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
